import { Mail, MessageCircle, ClipboardCheck, PhoneCall, Phone, FileText } from "lucide-react";
import { useSEO } from "../components/SEO";
import { PageHero, Reveal } from "../components/Reveal";
import { ContactForm } from "../components/ContactForm";
import { SITE, whatsappHref } from "../config/site";
import { trackEvent } from "../lib/analytics";

const NEXT_STEPS = [
  { icon: FileText, title: "We review your enquiry", text: "Every submission is read personally: your market, offer and challenge." },
  { icon: ClipboardCheck, title: "We assess the fit", text: "If Nexus Catalyst is right for your agency, we'll say so. If not, we'll say that too." },
  { icon: PhoneCall, title: "We schedule a strategy call", text: "A focused conversation about your biggest growth opportunity. No generic pitch." },
];

export default function Contact() {
  useSEO(
    "Contact Nexus Catalyst Agency",
    "Tell us about your Hajj & Umrah travel business, current marketing and growth goals. Request a strategy call or download the Free Customer Growth Blueprint."
  );

  const waHref = whatsappHref("#enquiry-form");
  const waExternal = waHref.startsWith("http");

  return (
    <main data-testid="contact-page">
      <PageHero
        tag="Contact"
        testid="contact-hero"
        crumbs={[{ label: "Contact", to: "/contact" }]}
        lines={[
          "Let's find your biggest",
          <><span className="text-tan">growth opportunity.</span></>,
        ]}
        copy="Tell us about your Hajj & Umrah business, current marketing and growth goals. We'll review the opportunity and determine whether Nexus Catalyst is a good fit."
      />

      <section className="bg-cream py-20 md:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <div id="enquiry-form" className="bg-white border border-charcoal/10 p-8 md:p-12 scroll-mt-28">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-charcoal mb-2">
                Request a Strategy Call
              </h2>
              <p className="text-sm text-charcoal/55 mb-10">
                Two minutes of honest detail helps us give you a genuinely
                useful first conversation.
              </p>
              <ContactForm />
            </div>
          </Reveal>

          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="bg-charcoal p-8" data-testid="contact-email-card">
                <Mail size={20} strokeWidth={1.5} className="text-gold" />
                <h3 className="mt-4 text-lg font-semibold tracking-tight text-cream">
                  Prefer email?
                </h3>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-email-link"
                  className="mt-2 inline-block text-sm text-cream/70 hover:text-gold transition-colors break-all"
                >
                  {SITE.email}
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="bg-white border border-charcoal/10 p-8" data-testid="contact-phone-card">
                <Phone size={20} strokeWidth={1.5} className="text-tan" />
                <h3 className="mt-4 text-lg font-semibold tracking-tight text-charcoal">
                  Prefer to call?
                </h3>
                <a
                  href={`tel:${SITE.phone}`}
                  data-testid="contact-phone-link"
                  className="mt-2 inline-block text-sm text-charcoal/70 hover:text-tan transition-colors"
                >
                  {SITE.phoneDisplay}
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="bg-white border border-charcoal/10 p-8" data-testid="contact-whatsapp-card">
                <MessageCircle size={20} strokeWidth={1.5} className="text-tan" />
                <h3 className="mt-4 text-lg font-semibold tracking-tight text-charcoal">
                  Start a WhatsApp Conversation
                </h3>
                <p className="mt-2 text-sm text-charcoal/55 leading-relaxed">
                  The fastest way to reach us: the same channel your customers
                  use to reach you.
                </p>
                {waExternal ? (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-button"
                    onClick={() => trackEvent("whatsapp_click", { placement: "contact_page" })}
                    className="mt-5 inline-flex items-center gap-2 border border-charcoal/25 text-charcoal text-sm font-semibold px-6 py-3 hover:border-gold hover:text-tan transition-colors"
                  >
                    <MessageCircle size={15} strokeWidth={1.5} />
                    Start a WhatsApp Conversation
                  </a>
                ) : (
                  <a
                    href={waHref}
                    data-testid="contact-whatsapp-button"
                    onClick={() => trackEvent("whatsapp_click", { placement: "contact_page", fallback: true })}
                    className="mt-5 inline-flex items-center gap-2 border border-charcoal/25 text-charcoal text-sm font-semibold px-6 py-3 hover:border-gold hover:text-tan transition-colors"
                  >
                    <MessageCircle size={15} strokeWidth={1.5} />
                    Start a WhatsApp Conversation
                  </a>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-white border border-charcoal/10 p-8" data-testid="contact-next-steps">
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-6">
                  What Happens Next
                </div>
                <div className="space-y-6">
                  {NEXT_STEPS.map((s, i) => (
                    <div key={s.title} className="flex items-start gap-4" data-testid={`contact-step-${i + 1}`}>
                      <s.icon size={18} strokeWidth={1.5} className="text-gold mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-charcoal">{s.title}</div>
                        <div className="text-sm text-charcoal/55 mt-1 leading-relaxed">{s.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
